import { AI_SCRIPT_CHAT_CONTEXT_KIND } from "@/payroll/ai-script/types";
import type { AiScriptChatSelectionContext } from "@/payroll/ai-script/types";

export const CELL_FIGURE_QUESTION = "How was this figure calculated?";

export const hasSelectionContextContent = (
  context: AiScriptChatSelectionContext,
) => {
  switch (context.kind) {
    case AI_SCRIPT_CHAT_CONTEXT_KIND.cell:
      return context.value != null;
    case AI_SCRIPT_CHAT_CONTEXT_KIND.row:
      return context.cells.length > 0;
    case AI_SCRIPT_CHAT_CONTEXT_KIND.column:
      return context.values.length > 0;
  }
};

export const formatSelectionContextTitle = (
  context: AiScriptChatSelectionContext,
) => {
  switch (context.kind) {
    case AI_SCRIPT_CHAT_CONTEXT_KIND.cell:
      return `${context.sheetName} · ${context.coordinate}`;
    case AI_SCRIPT_CHAT_CONTEXT_KIND.row:
      return `${context.sheetName} · Row ${context.rowNumber}`;
    case AI_SCRIPT_CHAT_CONTEXT_KIND.column:
      return `${context.sheetName} · Column ${context.columnLetter}`;
  }
};

export const formatSelectionContextChip = (
  context: AiScriptChatSelectionContext,
) => {
  switch (context.kind) {
    case AI_SCRIPT_CHAT_CONTEXT_KIND.cell: {
      const labels = [context.rowLabel, context.columnHeader].filter(Boolean);
      const label = labels.length > 0 ? labels.join(" · ") : context.coordinate;

      return context.value != null ? `${label} — ${context.value}` : label;
    }
    case AI_SCRIPT_CHAT_CONTEXT_KIND.row:
      return context.rowLabel ?? `${context.cells.length} cells`;
    case AI_SCRIPT_CHAT_CONTEXT_KIND.column:
      return context.columnHeader ?? `${context.values.length} values`;
  }
};

export const formatSelectionContextSentence = (
  context: AiScriptChatSelectionContext,
) => {
  switch (context.kind) {
    case AI_SCRIPT_CHAT_CONTEXT_KIND.cell: {
      const parts = [
        `cell ${context.coordinate} on the "${context.sheetName}" sheet`,
      ];
      if (context.columnHeader) parts.push(`column "${context.columnHeader}"`);
      if (context.rowLabel) parts.push(`row "${context.rowLabel}"`);
      if (context.value != null) parts.push(`showing ${context.value}`);

      return `(I'm looking at ${parts.join(", ")}.)`;
    }
    case AI_SCRIPT_CHAT_CONTEXT_KIND.row: {
      const cells = context.cells
        .map((cell) =>
          cell.header ? `${cell.header}: ${cell.value}` : cell.value,
        )
        .join("; ");
      const label = context.rowLabel ? ` (${context.rowLabel})` : "";

      return `(I'm looking at row ${context.rowNumber}${label} on the "${context.sheetName}" sheet — ${cells}.)`;
    }
    case AI_SCRIPT_CHAT_CONTEXT_KIND.column: {
      const values = context.values
        .map((entry) =>
          entry.rowLabel ? `${entry.rowLabel}: ${entry.value}` : entry.value,
        )
        .join("; ");
      const header = context.columnHeader
        ? `the "${context.columnHeader}" column`
        : `column ${context.columnLetter}`;
      const truncatedNote = context.isTruncated ? " (first values shown)" : "";

      return `(I'm looking at ${header} on the "${context.sheetName}" sheet${truncatedNote} — ${values}.)`;
    }
  }
};
